// src/views/admin_sys/services/admin_services.jsx
import React, { useState, useEffect, useRef } from 'react'
import { servicesAPI, usersAPI } from 'services/api.js'

// ─── PAGINATION HOOK ──────────────────────────────────────────────────────────
const usePagination = (data, pageSize = 10) => {
    const [page, setPage] = useState(1)
    const totalPages = Math.max(1, Math.ceil(data.length / pageSize))
    const paginated = data.slice((page - 1) * pageSize, page * pageSize)
    const reset = () => setPage(1)
    return { page, setPage, totalPages, paginated, reset }
}

// ─── INLINE SVG ICONS ─────────────────────────────────────────────────────────
const PlusIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
)
const PencilIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
)
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
        <path d="M10 11v6"/><path d="M14 11v6"/>
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
)
const StarIcon = ({ filled }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
)
const ClockIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
    </svg>
)
const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="6 9 12 15 18 9"/>
    </svg>
)
const ChevronUpIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="18 15 12 9 6 15"/>
    </svg>
)
const UserIcon = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
)
const UsersIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
)
const InfoIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
)
const FileTextIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
    </svg>
)
const XIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
)
const SpinnerIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         style={{ animation: 'as-spin 0.8s linear infinite', display: 'inline-block' }}>
        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeOpacity="0.2"/>
        <path d="M21 12a9 9 0 00-9-9"/>
    </svg>
)
const ScissorsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/>
        <line x1="20" y1="4" x2="8.12" y2="15.88"/>
        <line x1="14.47" y1="14.48" x2="20" y2="20"/>
        <line x1="8.12" y1="8.12" x2="12" y2="12"/>
    </svg>
)
const ChevronLeftIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
)
const ChevronRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
)

// ─── GENDER BADGE ─────────────────────────────────────────────────────────────
const GenderBadge = ({ gender }) => {
    const cfgs = {
        male:   { label: 'Erkek',  cls: 'badge-male' },
        female: { label: 'Kadın',  cls: 'badge-female' },
        unisex: { label: 'Unisex', cls: 'badge-unisex' },
    }
    const { label, cls } = cfgs[gender] || cfgs.unisex
    return <span className={`as-badge ${cls}`}>{label}</span>
}

// ─── SKELETON ─────────────────────────────────────────────────────────────────
const SkeletonRow = () => (
    <tr>
        {[40, 200, 80, 90, 70, 100].map((w, i) => (
            <td key={i} style={{ padding: '14px 16px' }}>
                <div className="as-skeleton" style={{ width: w, height: 16, borderRadius: 6 }} />
            </td>
        ))}
    </tr>
)
const SkeletonCard = () => (
    <div className="as-mobile-card">
        <div className="d-flex justify-content-between mb-3">
            <div className="as-skeleton" style={{ width: 140, height: 18, borderRadius: 6 }} />
            <div className="as-skeleton" style={{ width: 60, height: 24, borderRadius: 20 }} />
        </div>
        <div className="d-flex gap-2 mb-3">
            <div className="as-skeleton" style={{ width: 70, height: 22, borderRadius: 20 }} />
            <div className="as-skeleton" style={{ width: 60, height: 22, borderRadius: 20 }} />
        </div>
        <div className="d-flex justify-content-end gap-2">
            <div className="as-skeleton" style={{ width: 72, height: 32, borderRadius: 8 }} />
            <div className="as-skeleton" style={{ width: 60, height: 32, borderRadius: 8 }} />
        </div>
    </div>
)

// ─── PAGINATION COMPONENT ─────────────────────────────────────────────────────
const Pagination = ({ page, totalPages, onPage }) => {
    if (totalPages <= 1) return null
    const pages = []
    for (let i = 1; i <= totalPages; i++) pages.push(i)
    return (
        <div className="as-pagination">
            <button className="as-page-btn" onClick={() => onPage(page - 1)} disabled={page === 1}>
                <ChevronLeftIcon />
            </button>
            {pages.map(p => (
                <button
                    key={p}
                    className={`as-page-btn ${p === page ? 'active' : ''}`}
                    onClick={() => onPage(p)}
                >
                    {p}
                </button>
            ))}
            <button className="as-page-btn" onClick={() => onPage(page + 1)} disabled={page === totalPages}>
                <ChevronRightIcon />
            </button>
        </div>
    )
}

// ─── MODAL COMPONENT ──────────────────────────────────────────────────────────
const Modal = ({ visible, onClose, title, children, footer, size = 'md' }) => {
    const overlayRef = useRef(null)
    useEffect(() => {
        const onKey = (e) => e.key === 'Escape' && onClose()
        if (visible) document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [visible, onClose])

    if (!visible) return null
    return (
        <div className="as-modal-overlay" ref={overlayRef} onClick={(e) => e.target === overlayRef.current && onClose()}>
            <div className={`as-modal as-modal-${size}`} style={{ animation: 'as-modal-in 0.2s ease both' }}>
                <div className="as-modal-header">
                    <span className="as-modal-title">{title}</span>
                    <button className="as-modal-close" onClick={onClose}><XIcon /></button>
                </div>
                <div className="as-modal-body">{children}</div>
                {footer && <div className="as-modal-footer">{footer}</div>}
            </div>
        </div>
    )
}

// ─── TOGGLE SWITCH ────────────────────────────────────────────────────────────
const Toggle = ({ id, checked, onChange, label, sublabel }) => (
    <div className="as-toggle-row">
        <label className="as-toggle-switch" htmlFor={id}>
            <input id={id} type="checkbox" checked={checked} onChange={onChange} />
            <span className="as-toggle-track">
        <span className="as-toggle-thumb" />
      </span>
        </label>
        <div>
            <span className="as-toggle-label">{label}</span>
            {sublabel && <span className="as-toggle-sub">{sublabel}</span>}
        </div>
    </div>
)

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

    .as-page {
      font-family: 'Plus Jakarta Sans', sans-serif;
      --as-accent:      #7c5cfc;
      --as-accent-l:    rgba(124,92,252,0.12);
      --as-accent-h:    #6b4edb;
      --as-success:     #12b886;
      --as-success-l:   rgba(18,184,134,0.12);
      --as-error:       #fa5252;
      --as-error-l:     rgba(250,82,82,0.12);
      --as-warn:        #f59e0b;
      --as-warn-l:      rgba(245,158,11,0.12);
      --as-info:        #3b82f6;
      --as-info-l:      rgba(59,130,246,0.12);
      --as-pink:        #ec4899;
      --as-pink-l:      rgba(236,72,153,0.12);

      /* light */
      --as-bg:          #f6f5ff;
      --as-card:        #ffffff;
      --as-card2:       #f2f0ff;
      --as-border:      rgba(124,92,252,0.12);
      --as-border2:     rgba(0,0,0,0.07);
      --as-text1:       #1a1a2e;
      --as-text2:       #6b7280;
      --as-text3:       #9ca3af;
      --as-shadow:      0 4px 24px rgba(124,92,252,0.07);
      --as-shadow-lg:   0 12px 40px rgba(124,92,252,0.13);
      --as-skel1:       rgba(124,92,252,0.08);
      --as-skel2:       rgba(124,92,252,0.16);
      --as-head-bg:     #f9f8ff;
    }

    /* ── DARK MODE ── */
    [data-coreui-theme="dark"] .as-page,
    [data-bs-theme="dark"] .as-page,
    .dark .as-page {
      --as-bg:          #0f0e17;
      --as-card:        #1a1928;
      --as-card2:       #1f1e2e;
      --as-border:      rgba(124,92,252,0.18);
      --as-border2:     rgba(255,255,255,0.06);
      --as-text1:       #f0eeff;
      --as-text2:       #a09bc0;
      --as-text3:       #6b6785;
      --as-shadow:      0 4px 24px rgba(0,0,0,0.35);
      --as-shadow-lg:   0 12px 40px rgba(0,0,0,0.5);
      --as-skel1:       rgba(124,92,252,0.08);
      --as-skel2:       rgba(124,92,252,0.18);
      --as-head-bg:     #16152a;
    }

    /* ── ANIMATIONS ── */
    @keyframes as-spin  { to { transform: rotate(360deg); } }
    @keyframes as-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes as-modal-in {
      from { opacity: 0; transform: translateY(-16px) scale(0.97); }
      to   { opacity: 1; transform: none; }
    }
    @keyframes as-slide-down {
      from { opacity: 0; transform: translateY(-8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes as-fade { from { opacity:0; } to { opacity:1; } }

    /* ── HEADER CARD ── */
    .as-header-card {
      background: var(--as-card);
      border-radius: 20px;
      border: 1px solid var(--as-border);
      box-shadow: var(--as-shadow);
      padding: 20px 24px;
      display: flex; align-items: center; justify-content: space-between;
      flex-wrap: wrap; gap: 12px;
      margin-bottom: 16px;
    }
    .as-header-left { display: flex; align-items: center; gap: 14px; }
    .as-header-icon {
      width: 48px; height: 48px; border-radius: 14px;
      background: var(--as-accent-l);
      display: flex; align-items: center; justify-content: center;
      color: var(--as-accent); flex-shrink: 0;
    }
    .as-header-title { font-size: 1.15rem; font-weight: 800; color: var(--as-text1); margin: 0; line-height: 1.2; }
    .as-header-sub   { font-size: 0.78rem; color: var(--as-text3); margin: 2px 0 0; font-weight: 400; }

    /* ── MAIN CARD ── */
    .as-main-card {
      background: var(--as-card);
      border-radius: 20px;
      border: 1px solid var(--as-border);
      box-shadow: var(--as-shadow);
      overflow: hidden;
    }

    /* ── SEARCH / FILTER BAR ── */
    .as-toolbar {
      padding: 16px 20px;
      border-bottom: 1px solid var(--as-border2);
      display: flex; gap: 10px; flex-wrap: wrap;
      align-items: center;
    }
    .as-search-wrap {
      position: relative; flex: 1; min-width: 160px;
    }
    .as-search-wrap svg {
      position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
      color: var(--as-text3); pointer-events: none;
    }
    .as-search {
      width: 100%; padding: 9px 12px 9px 36px;
      background: var(--as-card2);
      border: 1.5px solid var(--as-border);
      border-radius: 10px;
      color: var(--as-text1);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.82rem; outline: none;
      transition: border-color 0.2s;
    }
    .as-search:focus { border-color: var(--as-accent); }
    .as-search::placeholder { color: var(--as-text3); }
    .as-filter-select {
      padding: 9px 30px 9px 12px;
      background: var(--as-card2);
      border: 1.5px solid var(--as-border);
      border-radius: 10px;
      color: var(--as-text2);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.82rem; outline: none; cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%239ca3af' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 10px center;
      transition: border-color 0.2s;
    }
    .as-filter-select:focus { border-color: var(--as-accent); }

    /* ── TABLE ── */
    .as-table-wrap { overflow-x: auto; }
    .as-table {
      width: 100%; border-collapse: collapse;
    }
    .as-table thead tr {
      background: var(--as-head-bg);
      border-bottom: 1.5px solid var(--as-border2);
    }
    .as-table thead th {
      padding: 12px 16px;
      font-size: 0.7rem; font-weight: 700;
      color: var(--as-text3); text-transform: uppercase; letter-spacing: 0.6px;
      white-space: nowrap;
    }
    .as-table tbody tr { border-bottom: 1px solid var(--as-border2); transition: background 0.15s; }
    .as-table tbody tr:last-child { border-bottom: none; }
    .as-table tbody tr.as-tr-main:hover { background: var(--as-card2); cursor: pointer; }
    .as-table tbody td { padding: 13px 16px; font-size: 0.85rem; color: var(--as-text1); vertical-align: middle; }
    .as-expand-row td { padding: 0; background: var(--as-head-bg); }
    .as-expand-inner {
      animation: as-slide-down 0.2s ease both;
      padding: 12px 16px 14px 52px;
      border-top: 1px solid var(--as-border2);
    }

    /* ── SERVICE NAME CELL ── */
    .as-service-name { font-weight: 700; color: var(--as-text1); display: flex; align-items: center; gap: 7px; }
    .as-service-desc { font-size: 0.72rem; color: var(--as-text3); margin-top: 2px; }
    .as-price-text { font-weight: 800; color: var(--as-text1); font-size: 0.9rem; }

    /* ── BADGES ── */
    .as-badge {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 10px; border-radius: 20px;
      font-size: 0.7rem; font-weight: 700;
    }
    .badge-male   { background: var(--as-info-l);    color: var(--as-info); }
    .badge-female { background: var(--as-pink-l);    color: var(--as-pink); }
    .badge-unisex { background: var(--as-success-l); color: var(--as-success); }
    .as-badge-opt  { background: var(--as-warn-l);   color: var(--as-warn); padding: 3px 8px; border-radius: 20px; font-size: 0.65rem; font-weight: 700; }
    .as-badge-custom { background: var(--as-info-l); color: var(--as-info); padding: 3px 8px; border-radius: 20px; font-size: 0.65rem; font-weight: 700; display: inline-flex; align-items: center; gap: 4px; }
    .as-badge-fav { color: var(--as-warn); cursor: pointer; background: none; border: none; padding: 2px; display: inline-flex; transition: transform 0.15s; }
    .as-badge-fav:hover { transform: scale(1.25); }

    /* ── ICON BUTTONS ── */
    .as-icon-btn {
      width: 32px; height: 32px; border-radius: 8px;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1.5px solid var(--as-border);
      background: var(--as-card2); color: var(--as-text2);
      cursor: pointer; transition: all 0.18s; font-size: 0; padding: 0;
      font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .as-icon-btn:hover { border-color: var(--as-accent); color: var(--as-accent); background: var(--as-accent-l); }
    .as-icon-btn.danger:hover { border-color: var(--as-error); color: var(--as-error); background: var(--as-error-l); }
    .as-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    /* ── BUTTONS ── */
    .as-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 7px;
      padding: 9px 18px; border-radius: 11px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.82rem; font-weight: 700;
      cursor: pointer; transition: all 0.18s;
      border: none; white-space: nowrap;
    }
    .as-btn-primary { background: var(--as-accent); color: #fff; }
    .as-btn-primary:hover:not(:disabled) { background: var(--as-accent-h); box-shadow: 0 4px 16px rgba(124,92,252,0.3); transform: translateY(-1px); }
    .as-btn-ghost   { background: var(--as-card2); color: var(--as-text2); border: 1.5px solid var(--as-border); }
    .as-btn-ghost:hover:not(:disabled) { border-color: var(--as-accent); color: var(--as-accent); }
    .as-btn-danger  { background: var(--as-error); color: #fff; }
    .as-btn-danger:hover:not(:disabled) { background: #e03131; box-shadow: 0 4px 16px rgba(250,82,82,0.3); transform: translateY(-1px); }
    .as-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; box-shadow: none !important; }

    /* ── MOBILE CARDS ── */
    .as-mobile-list { display: flex; flex-direction: column; gap: 10px; padding: 12px; }
    .as-mobile-card {
      background: var(--as-card2);
      border-radius: 16px;
      border: 1.5px solid var(--as-border);
      overflow: hidden;
      position: relative;
      transition: border-color 0.2s;
    }
    .as-mobile-card:hover { border-color: rgba(124,92,252,0.3); }
    .as-mobile-card-strip {
      position: absolute; top: 0; left: 0; bottom: 0;
      width: 4px; border-radius: 4px 0 0 4px;
    }
    .as-mobile-card-body { padding: 14px 14px 12px 18px; }
    .as-mobile-card-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 8px; }
    .as-mobile-card-title { font-size: 0.92rem; font-weight: 800; color: var(--as-text1); display: flex; align-items: center; gap: 6px; }
    .as-mobile-card-price { font-size: 1rem; font-weight: 800; color: var(--as-accent); }
    .as-mobile-card-meta  { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; align-items: center; }
    .as-mobile-expand-content {
      padding: 10px 14px 12px 18px;
      background: var(--as-card);
      border-top: 1px solid var(--as-border2);
      animation: as-slide-down 0.2s ease both;
    }
    .as-mobile-card-actions {
      display: flex; justify-content: flex-end; gap: 8px;
      padding-top: 10px; border-top: 1px solid var(--as-border2);
    }
    .as-mobile-action-btn {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 6px 14px; border-radius: 8px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.78rem; font-weight: 700;
      cursor: pointer; transition: all 0.18s;
      border: 1.5px solid var(--as-border);
      background: var(--as-card); color: var(--as-text2);
    }
    .as-mobile-action-btn.edit:hover { border-color: var(--as-accent); color: var(--as-accent); background: var(--as-accent-l); }
    .as-mobile-action-btn.del:hover  { border-color: var(--as-error);  color: var(--as-error);  background: var(--as-error-l); }

    /* ── CUSTOM STAFF PRICING CARDS (EXPAND) ── */
    .as-staff-pricing-grid { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
    .as-staff-price-card {
      background: var(--as-card);
      border-radius: 10px; border: 1px solid var(--as-border2);
      padding: 8px 12px; min-width: 160px;
      display: flex; flex-direction: column; gap: 4px;
    }
    .as-staff-price-name { font-size: 0.78rem; font-weight: 700; color: var(--as-text1); display: flex; align-items: center; gap: 5px; }
    .as-staff-price-vals { display: flex; justify-content: space-between; align-items: center; }
    .as-staff-price-amount { font-size: 0.82rem; font-weight: 800; color: var(--as-success); }
    .as-staff-price-dur   { font-size: 0.75rem; color: var(--as-text3); display: flex; align-items: center; gap: 3px; }

    /* ── SKELETON ── */
    .as-skeleton {
      background: linear-gradient(90deg, var(--as-skel1) 25%, var(--as-skel2) 50%, var(--as-skel1) 75%);
      background-size: 200% 100%;
      animation: as-shimmer 1.5s infinite;
      border-radius: 6px;
    }

    /* ── PAGINATION ── */
    .as-pagination {
      display: flex; align-items: center; justify-content: center; gap: 4px;
      padding: 16px 20px;
      border-top: 1px solid var(--as-border2);
    }
    .as-page-btn {
      min-width: 34px; height: 34px; border-radius: 9px;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1.5px solid var(--as-border);
      background: var(--as-card2); color: var(--as-text2);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.78rem; font-weight: 700;
      cursor: pointer; transition: all 0.15s; padding: 0 8px;
    }
    .as-page-btn:hover:not(:disabled):not(.active) { border-color: var(--as-accent); color: var(--as-accent); background: var(--as-accent-l); }
    .as-page-btn.active { background: var(--as-accent); border-color: var(--as-accent); color: #fff; }
    .as-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }

    /* ── MODAL ── */
    .as-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(0,0,0,0.55); backdrop-filter: blur(4px);
      z-index: 1050;
      display: flex; align-items: center; justify-content: center;
      padding: 16px;
    }
    .as-modal {
      background: var(--as-card);
      border-radius: 20px;
      border: 1px solid var(--as-border);
      box-shadow: var(--as-shadow-lg);
      display: flex; flex-direction: column;
      max-height: 90vh;
      overflow: hidden;
      width: 100%;
    }
    .as-modal-md { max-width: 540px; }
    .as-modal-lg { max-width: 700px; }
    .as-modal-sm { max-width: 400px; }
    .as-modal-header {
      padding: 18px 22px;
      border-bottom: 1px solid var(--as-border2);
      display: flex; align-items: center; justify-content: space-between;
      flex-shrink: 0;
    }
    .as-modal-title { font-size: 1rem; font-weight: 800; color: var(--as-text1); }
    .as-modal-close {
      width: 32px; height: 32px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center;
      background: var(--as-card2); border: none; cursor: pointer;
      color: var(--as-text3); transition: all 0.15s;
    }
    .as-modal-close:hover { background: var(--as-error-l); color: var(--as-error); }
    .as-modal-body { padding: 22px; overflow-y: auto; flex: 1; }
    .as-modal-footer {
      padding: 14px 22px;
      border-top: 1px solid var(--as-border2);
      display: flex; justify-content: flex-end; gap: 10px;
      flex-shrink: 0;
    }

    /* ── TABS ── */
    .as-tabs { display: flex; gap: 4px; margin-bottom: 20px; background: var(--as-card2); border: 1px solid var(--as-border); border-radius: 12px; padding: 4px; }
    .as-tab-btn {
      flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
      padding: 9px 12px; border-radius: 9px;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.8rem; font-weight: 700;
      border: none; cursor: pointer;
      transition: all 0.2s;
      background: none; color: var(--as-text3);
    }
    .as-tab-btn.active { background: var(--as-card); color: var(--as-accent); box-shadow: 0 2px 8px rgba(124,92,252,0.12); }
    .as-tab-btn:not(.active):hover { color: var(--as-text1); }

    /* ── FORM ELEMENTS ── */
    .as-form-group { margin-bottom: 16px; }
    .as-label {
      display: block; margin-bottom: 7px;
      font-size: 0.7rem; font-weight: 700; letter-spacing: 0.6px;
      color: var(--as-text3); text-transform: uppercase;
    }
    .as-input, .as-textarea {
      width: 100%; padding: 10px 13px;
      background: var(--as-card2);
      border: 1.5px solid var(--as-border);
      border-radius: 10px;
      color: var(--as-text1);
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.85rem; outline: none;
      transition: border-color 0.2s;
      box-sizing: border-box;
    }
    .as-input:focus, .as-textarea:focus { border-color: var(--as-accent); }
    .as-input::placeholder, .as-textarea::placeholder { color: var(--as-text3); }
    .as-textarea { resize: none; }
    .as-input-group { display: flex; }
    .as-input-group .as-prefix {
      padding: 10px 12px;
      background: var(--as-card2); border: 1.5px solid var(--as-border); border-right: none;
      border-radius: 10px 0 0 10px;
      color: var(--as-text3); font-size: 0.82rem; font-weight: 600;
      display: flex; align-items: center;
    }
    .as-input-group .as-input { border-radius: 0 10px 10px 0; }
    .as-input-group .as-input:focus { border-color: var(--as-accent); }

    /* ── GENDER SELECTOR ── */
    .as-gender-group { display: flex; gap: 8px; }
    .as-gender-opt {
      flex: 1; padding: 10px 8px; border-radius: 10px;
      border: 1.5px solid var(--as-border);
      background: var(--as-card2); color: var(--as-text3);
      cursor: pointer; text-align: center;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 0.78rem; font-weight: 700;
      transition: all 0.2s;
    }
    .as-gender-opt:hover { border-color: var(--as-accent); }
    .as-gender-opt.active-unisex { border-color: var(--as-success); color: var(--as-success); background: var(--as-success-l); }
    .as-gender-opt.active-male   { border-color: var(--as-info);    color: var(--as-info);    background: var(--as-info-l); }
    .as-gender-opt.active-female { border-color: var(--as-pink);    color: var(--as-pink);    background: var(--as-pink-l); }

    /* ── TOGGLE ── */
    .as-toggle-row {
      display: flex; align-items: flex-start; gap: 12px;
      background: var(--as-card2); border: 1.5px solid var(--as-border);
      border-radius: 12px; padding: 12px 14px;
    }
    .as-toggle-switch { position: relative; display: inline-block; flex-shrink: 0; margin-top: 2px; }
    .as-toggle-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
    .as-toggle-track {
      display: block; width: 40px; height: 22px;
      background: var(--as-border); border-radius: 20px;
      cursor: pointer; transition: background 0.2s;
    }
    .as-toggle-switch input:checked + .as-toggle-track { background: var(--as-accent); }
    .as-toggle-thumb {
      position: absolute; top: 3px; left: 3px;
      width: 16px; height: 16px; border-radius: 50%;
      background: #fff; transition: transform 0.2s;
      box-shadow: 0 1px 4px rgba(0,0,0,0.2);
    }
    .as-toggle-switch input:checked ~ .as-toggle-track .as-toggle-thumb { transform: translateX(18px); }
    .as-toggle-label { font-size: 0.85rem; font-weight: 700; color: var(--as-text1); display: block; }
    .as-toggle-sub   { font-size: 0.72rem; color: var(--as-text3); display: block; margin-top: 2px; }

    /* ── STAFF PRICING TABLE (MODAL) ── */
    .as-staff-table { width: 100%; border-collapse: collapse; }
    .as-staff-table th {
      padding: 9px 12px; font-size: 0.68rem; font-weight: 700;
      color: var(--as-text3); text-transform: uppercase; letter-spacing: 0.5px;
      border-bottom: 1px solid var(--as-border2); background: var(--as-head-bg);
      text-align: left;
    }
    .as-staff-table td { padding: 9px 12px; border-bottom: 1px solid var(--as-border2); vertical-align: middle; }
    .as-staff-table tr:last-child td { border-bottom: none; }
    .as-staff-table-wrap { border: 1px solid var(--as-border); border-radius: 12px; overflow: hidden; }

    /* ── INFO BOX ── */
    .as-info-box {
      background: rgba(124,92,252,0.08); border: 1px solid rgba(124,92,252,0.2);
      border-radius: 10px; padding: 10px 14px;
      display: flex; gap: 8px; align-items: flex-start;
      font-size: 0.78rem; color: var(--as-text2);
      margin-bottom: 16px;
    }
    .as-info-box svg { color: var(--as-accent); flex-shrink: 0; margin-top: 1px; }

    /* ── EMPTY STATE ── */
    .as-empty {
      display: flex; flex-direction: column; align-items: center;
      padding: 56px 24px; text-align: center;
    }
    .as-empty-icon {
      width: 64px; height: 64px; border-radius: 20px;
      background: var(--as-accent-l); color: var(--as-accent);
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 16px;
    }
    .as-empty-title { font-size: 1rem; font-weight: 800; color: var(--as-text1); margin: 0 0 6px; }
    .as-empty-sub   { font-size: 0.82rem; color: var(--as-text3); margin: 0 0 20px; }

    /* ── CHEVRON EXPAND BUTTON ── */
    .as-expand-btn {
      width: 26px; height: 26px; border-radius: 7px;
      background: var(--as-card2); border: 1px solid var(--as-border);
      display: inline-flex; align-items: center; justify-content: center;
      color: var(--as-text3); cursor: pointer; transition: all 0.15s; padding: 0;
    }
    .as-expand-btn:hover { background: var(--as-accent-l); color: var(--as-accent); border-color: var(--as-accent); }

    /* ── RESULTS COUNT ── */
    .as-results-count {
      font-size: 0.75rem; color: var(--as-text3); font-weight: 500;
      padding: 8px 20px 0;
    }

    /* ── DELETE MODAL ── */
    .as-delete-icon {
      width: 68px; height: 68px; border-radius: 50%;
      background: var(--as-error-l); color: var(--as-error);
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 16px;
    }
    .as-delete-title { font-size: 1.1rem; font-weight: 800; color: var(--as-text1); margin: 0 0 8px; text-align: center; }
    .as-delete-sub { font-size: 0.85rem; color: var(--as-text2); text-align: center; margin: 0 0 4px; }
  `}</style>
)

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AdminServices = () => {
    const [services,         setServices]         = useState([])
    const [staffList,        setStaffList]         = useState([])
    const [loading,          setLoading]           = useState(true)
    const [saving,           setSaving]            = useState(false)
    const [deleting,         setDeleting]          = useState(false)

    const [expandedId,       setExpandedId]        = useState(null)

    const [isModalVisible,   setIsModalVisible]    = useState(false)
    const [modalMode,        setModalMode]         = useState('add')
    const [activeTab,        setActiveTab]         = useState(1)

    const [currentService, setCurrentService] = useState({
        id: null, name: '', price: 0, duration: 30,
        description: '', optional: false, target_gender: 'unisex', is_favorite: false,
    })

    const [staffPricingList, setStaffPricingList]  = useState([])

    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
    const [serviceToDelete,      setServiceToDelete]      = useState(null)

    // Filter & search state
    const [search,      setSearch]      = useState('')
    const [filterType,  setFilterType]  = useState('all')    // all | main | optional
    const [filterGender, setFilterGender] = useState('all')  // all | male | female | unisex

    // ── Initial Fetch
    useEffect(() => { fetchInitialData() }, [])

    const fetchInitialData = async () => {
        setLoading(true)
        try {
            const [servicesRes, staffRes] = await Promise.all([
                servicesAPI.getAll(),
                usersAPI.getStaff(),
            ])
            if (servicesRes.success) setServices(servicesRes.services || [])
            if (staffRes.success)    setStaffList(staffRes.staff || [])
        } catch (err) {
            console.error('Veriler yüklenirken hata:', err)
        } finally {
            setLoading(false)
        }
    }

    // ── Derived / filtered list
    const sortedServices = [...services].sort(
        (a, b) => (b.is_favorite === true ? 1 : 0) - (a.is_favorite === true ? 1 : 0)
    )

    const filteredServices = sortedServices.filter(s => {
        const matchSearch = s.name.toLowerCase().includes(search.toLowerCase())
        const matchType   = filterType === 'all'
            ? true : filterType === 'optional' ? s.optional : !s.optional
        const matchGender = filterGender === 'all' ? true : s.target_gender === filterGender
        return matchSearch && matchType && matchGender
    })

    // Pagination (10 per page)
    const PAGE_SIZE = 10
    const { page, setPage, totalPages, paginated: paginatedServices, reset: resetPage } = usePagination(filteredServices, PAGE_SIZE)

    // Reset to page 1 on filter change
    useEffect(() => { resetPage() }, [search, filterType, filterGender])

    // ── Handlers
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setCurrentService(prev => ({
            ...prev,
            [name]: type === 'checkbox'
                ? checked
                : (name === 'price' || name === 'duration' ? Number(value) : value),
        }))
    }

    const handleStaffPriceChange = (staffId, field, value) => {
        setStaffPricingList(prev =>
            prev.map(item =>
                item.staff_id === staffId
                    ? { ...item, [field]: value === '' ? null : Number(value) }
                    : item
            )
        )
    }

    const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id)

    const handleShowAddModal = () => {
        setModalMode('add')
        setActiveTab(1)
        setCurrentService({ id: null, name: '', price: 0, duration: 30, description: '', optional: false, target_gender: 'unisex', is_favorite: false })
        setStaffPricingList(staffList.map(s => ({
            staff_id: s.id,
            staff_name: `${s.name} ${s.surname}`,
            custom_price: null,
            custom_duration: null,
        })))
        setIsModalVisible(true)
    }

    const handleShowEditModal = (service) => {
        setModalMode('edit')
        setActiveTab(1)
        setCurrentService({ ...service, target_gender: service.target_gender || 'unisex', is_favorite: service.is_favorite || false })
        setStaffPricingList(staffList.map(s => {
            const pivot = service.staff?.find(u => u.id === s.id)?.pivot
            return {
                staff_id: s.id,
                staff_name: `${s.name} ${s.surname}`,
                custom_price:    pivot?.price    ? Number(pivot.price)    : null,
                custom_duration: pivot?.duration ? Number(pivot.duration) : null,
            }
        }))
        setIsModalVisible(true)
    }

    const handleSave = () => {
        if (!currentService.name.trim()) return alert('Hizmet adı zorunludur.')
        const filteredStaffPrices = staffPricingList.filter(
            sp => sp.custom_price !== null || sp.custom_duration !== null
        )
        const payload = { ...currentService, staff_prices: filteredStaffPrices }
        setSaving(true)
        const apiCall = modalMode === 'add'
            ? servicesAPI.create(payload)
            : servicesAPI.update(currentService.id, payload)
        apiCall
            .then(() => { setIsModalVisible(false); fetchInitialData() })
            .catch(() => alert('İşlem sırasında bir hata oluştu. Lütfen tekrar deneyin.'))
            .finally(() => setSaving(false))
    }

    const handleToggleFavorite = async (service, e) => {
        e.stopPropagation()
        const updated = { ...service, is_favorite: !service.is_favorite }
        setServices(prev => prev.map(s => s.id === service.id ? updated : s))
        try {
            await servicesAPI.update(service.id, updated)
        } catch {
            setServices(prev => prev.map(s => s.id === service.id ? service : s))
        }
    }

    const handleDelete = () => {
        if (!serviceToDelete) return
        setDeleting(true)
        servicesAPI.delete(serviceToDelete.id)
            .then(() => {
                setIsDeleteModalVisible(false)
                setServices(prev => prev.filter(s => s.id !== serviceToDelete.id))
            })
            .catch(() => alert('Silme işleminde hata oluştu.'))
            .finally(() => setDeleting(false))
    }

    const stripColor = (gender) => ({
        female: '#ec4899',
        male:   '#3b82f6',
        unisex: '#12b886',
    })[gender] || '#12b886'

    // ─── RENDER ──────────────────────────────────────────────────────────────────
    return (
        <div className="as-page">
            <GlobalStyles />

            {/* ── HEADER ── */}
            <div className="as-header-card">
                <div className="as-header-left">
                    <div className="as-header-icon"><ScissorsIcon /></div>
                    <div>
                        <h1 className="as-header-title">Hizmet Yönetimi</h1>
                        <p className="as-header-sub">İşletmenizin sunduğu hizmetleri ve özel personel fiyatlarını yönetin.</p>
                    </div>
                </div>
                <button className="as-btn as-btn-primary" onClick={handleShowAddModal}>
                    <PlusIcon />
                    <span className="d-none d-sm-inline">Yeni Hizmet</span>
                    <span className="d-sm-none">Ekle</span>
                </button>
            </div>

            {/* ── MAIN CARD ── */}
            <div className="as-main-card">

                {/* ── TOOLBAR ── */}
                <div className="as-toolbar">
                    <div className="as-search-wrap">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input
                            className="as-search"
                            placeholder="Hizmet ara..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="as-filter-select"
                        value={filterType}
                        onChange={e => setFilterType(e.target.value)}
                    >
                        <option value="all">Tüm Türler</option>
                        <option value="main">Ana Hizmet</option>
                        <option value="optional">Opsiyonel</option>
                    </select>
                    <select
                        className="as-filter-select"
                        value={filterGender}
                        onChange={e => setFilterGender(e.target.value)}
                    >
                        <option value="all">Tüm Kitleler</option>
                        <option value="unisex">Unisex</option>
                        <option value="male">Erkek</option>
                        <option value="female">Kadın</option>
                    </select>
                </div>

                {loading && services.length === 0 ? (
                    <>
                        {/* ── SKELETON DESKTOP ── */}
                        <div className="d-none d-md-block as-table-wrap">
                            <table className="as-table">
                                <thead>
                                <tr>
                                    <th style={{ width: 40 }}></th>
                                    <th>Hizmet Adı</th>
                                    <th>Kategori</th>
                                    <th>Ücret</th>
                                    <th>Süre</th>
                                    <th style={{ textAlign: 'right' }}>İşlemler</th>
                                </tr>
                                </thead>
                                <tbody>{[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}</tbody>
                            </table>
                        </div>
                        {/* ── SKELETON MOBILE ── */}
                        <div className="d-md-none as-mobile-list">
                            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
                        </div>
                    </>
                ) : filteredServices.length === 0 ? (
                    <div className="as-empty">
                        <div className="as-empty-icon"><ScissorsIcon /></div>
                        <h3 className="as-empty-title">
                            {services.length === 0 ? 'Henüz hizmet eklenmemiş.' : 'Sonuç bulunamadı.'}
                        </h3>
                        <p className="as-empty-sub">
                            {services.length === 0
                                ? 'Başlamak için ilk hizmetinizi oluşturun.'
                                : 'Farklı arama veya filtre kriterlerini deneyin.'}
                        </p>
                        {services.length === 0 && (
                            <button className="as-btn as-btn-primary" onClick={handleShowAddModal}>
                                <PlusIcon /> İlk Hizmeti Ekle
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <p className="as-results-count">{filteredServices.length} hizmet</p>

                        {/* ── DESKTOP TABLE ── */}
                        <div className="d-none d-md-block as-table-wrap">
                            <table className="as-table">
                                <thead>
                                <tr>
                                    <th style={{ width: 44, paddingLeft: 20 }}></th>
                                    <th>Hizmet Adı & Detay</th>
                                    <th>Kategori</th>
                                    <th>Ücret</th>
                                    <th>Süre</th>
                                    <th style={{ textAlign: 'right', paddingRight: 20 }}>İşlemler</th>
                                </tr>
                                </thead>
                                <tbody>
                                {paginatedServices.map(service => {
                                    const isExpanded = expandedId === service.id
                                    const staffWithCustom = service.staff?.filter(u => u.pivot?.price || u.pivot?.duration) || []

                                    return (
                                        <React.Fragment key={service.id}>
                                            <tr
                                                className="as-tr-main"
                                                onClick={() => toggleExpand(service.id)}
                                            >
                                                {/* Expand chevron */}
                                                <td style={{ paddingLeft: 20, textAlign: 'center' }}>
                                                    <button className="as-expand-btn" tabIndex={-1}>
                                                        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                                    </button>
                                                </td>

                                                {/* Name & detail */}
                                                <td>
                                                    <div className="as-service-name">
                                                        <button
                                                            className="as-badge-fav"
                                                            style={{ color: service.is_favorite ? 'var(--as-warn)' : 'var(--as-text3)' }}
                                                            onClick={e => handleToggleFavorite(service, e)}
                                                            title={service.is_favorite ? 'Favoriden çıkar' : 'Favoriye ekle'}
                                                        >
                                                            <StarIcon filled={service.is_favorite} />
                                                        </button>
                                                        {service.name}
                                                        {service.optional && <span className="as-badge-opt">Opsiyonel</span>}
                                                        {staffWithCustom.length > 0 && (
                                                            <span className="as-badge-custom">
                                  <UsersIcon /> {staffWithCustom.length} Özel Fiyat
                                </span>
                                                        )}
                                                    </div>
                                                    {service.description && (
                                                        <div className="as-service-desc">{service.description}</div>
                                                    )}
                                                </td>

                                                {/* Gender */}
                                                <td><GenderBadge gender={service.target_gender} /></td>

                                                {/* Price */}
                                                <td>
                                                    <span className="as-price-text">₺{parseFloat(service.price).toFixed(2)}</span>
                                                </td>

                                                {/* Duration */}
                                                <td>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--as-text2)', fontSize: '0.82rem' }}>
                              <ClockIcon size={13} /> {service.duration} dk
                            </span>
                                                </td>

                                                {/* Actions */}
                                                <td style={{ textAlign: 'right', paddingRight: 20 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }} onClick={e => e.stopPropagation()}>
                                                        <button
                                                            className="as-icon-btn"
                                                            title="Düzenle"
                                                            onClick={() => handleShowEditModal(service)}
                                                        >
                                                            <PencilIcon />
                                                        </button>
                                                        <button
                                                            className="as-icon-btn danger"
                                                            title="Sil"
                                                            onClick={() => { setServiceToDelete(service); setIsDeleteModalVisible(true) }}
                                                        >
                                                            <TrashIcon />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>

                                            {/* Expand Row */}
                                            {isExpanded && (
                                                <tr className="as-expand-row">
                                                    <td colSpan={6}>
                                                        <div className="as-expand-inner">
                                                            {staffWithCustom.length > 0 ? (
                                                                <>
                                                                    <p style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--as-text3)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>
                                                                        Personel Özel Fiyatlandırmaları
                                                                    </p>
                                                                    <div className="as-staff-pricing-grid">
                                                                        {staffWithCustom.map(staff => (
                                                                            <div className="as-staff-price-card" key={staff.id}>
                                                                                <div className="as-staff-price-name">
                                                                                    <UserIcon size={12} /> {staff.name} {staff.surname}
                                                                                </div>
                                                                                <div className="as-staff-price-vals">
                                            <span className="as-staff-price-amount">
                                              ₺{parseFloat(staff.pivot.price || service.price).toFixed(0)}
                                            </span>
                                                                                    <span className="as-staff-price-dur">
                                              <ClockIcon size={11} /> {staff.pivot.duration || service.duration} dk
                                            </span>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <span style={{ fontSize: '0.8rem', color: 'var(--as-text3)', fontStyle: 'italic' }}>
                                    Bu hizmet için tanımlanmış özel personel fiyatı bulunmuyor.
                                  </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                                </tbody>
                            </table>
                        </div>

                        {/* ── MOBILE CARDS ── */}
                        <div className="d-md-none as-mobile-list">
                            {paginatedServices.map(service => {
                                const isExpanded = expandedId === service.id
                                const staffWithCustom = service.staff?.filter(u => u.pivot?.price || u.pivot?.duration) || []

                                return (
                                    <div className="as-mobile-card" key={service.id}>
                                        {/* Color strip */}
                                        <div
                                            className="as-mobile-card-strip"
                                            style={{ background: stripColor(service.target_gender) }}
                                        />
                                        <div className="as-mobile-card-body">
                                            {/* Top row */}
                                            <div
                                                className="as-mobile-card-top"
                                                onClick={() => toggleExpand(service.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div>
                                                    <div className="as-mobile-card-title">
                                                        <button
                                                            className="as-badge-fav"
                                                            style={{ color: service.is_favorite ? 'var(--as-warn)' : 'var(--as-text3)' }}
                                                            onClick={e => handleToggleFavorite(service, e)}
                                                        >
                                                            <StarIcon filled={service.is_favorite} />
                                                        </button>
                                                        {service.name}
                                                        <span style={{ color: 'var(--as-text3)', marginLeft: 2 }}>
                              {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </span>
                                                    </div>
                                                    {service.optional && (
                                                        <span className="as-badge-opt" style={{ marginTop: 4, display: 'inline-block' }}>Opsiyonel</span>
                                                    )}
                                                </div>
                                                <span className="as-mobile-card-price">₺{parseFloat(service.price).toFixed(0)}</span>
                                            </div>

                                            {/* Meta badges */}
                                            <div className="as-mobile-card-meta">
                                                <GenderBadge gender={service.target_gender} />
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', color: 'var(--as-text2)', background: 'var(--as-card)', border: '1px solid var(--as-border2)', padding: '3px 9px', borderRadius: 20, fontWeight: 600 }}>
                          <ClockIcon size={11} /> {service.duration} dk
                        </span>
                                                {staffWithCustom.length > 0 && (
                                                    <span className="as-badge-custom" style={{ marginLeft: 'auto' }}>
                            <UsersIcon /> {staffWithCustom.length} Özel
                          </span>
                                                )}
                                            </div>

                                            {/* Expand content */}
                                            {isExpanded && (
                                                <div className="as-mobile-expand-content">
                                                    <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--as-text3)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' }}>
                                                        Personel Özel Fiyatları
                                                    </p>
                                                    {staffWithCustom.length > 0 ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                            {staffWithCustom.map(staff => (
                                                                <div key={staff.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--as-card2)', padding: '7px 10px', borderRadius: 8 }}>
                                  <span style={{ fontSize: '0.78rem', color: 'var(--as-text1)', display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <UserIcon size={12} /> {staff.name}
                                  </span>
                                                                    <div style={{ display: 'flex', gap: 8, fontSize: '0.78rem', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 800, color: 'var(--as-success)' }}>
                                      ₺{parseFloat(staff.pivot.price || service.price).toFixed(0)}
                                    </span>
                                                                        <span style={{ color: 'var(--as-text3)', borderLeft: '1px solid var(--as-border2)', paddingLeft: 8 }}>
                                      {staff.pivot.duration || service.duration} dk
                                    </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: '0.78rem', color: 'var(--as-text3)', fontStyle: 'italic' }}>
                              Özel fiyat tanımlanmamış.
                            </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Actions */}
                                            <div className="as-mobile-card-actions">
                                                <button
                                                    className="as-mobile-action-btn edit"
                                                    onClick={() => handleShowEditModal(service)}
                                                >
                                                    <PencilIcon /> Düzenle
                                                </button>
                                                <button
                                                    className="as-mobile-action-btn del"
                                                    onClick={() => { setServiceToDelete(service); setIsDeleteModalVisible(true) }}
                                                >
                                                    <TrashIcon /> Sil
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {/* ── PAGINATION ── */}
                        <Pagination page={page} totalPages={totalPages} onPage={setPage} />
                    </>
                )}
            </div>

            {/* ─────────────────────────────────────────────────────────────────────────
         EKLE / DÜZENLE MODAL
      ───────────────────────────────────────────────────────────────────────── */}
            <Modal
                visible={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                size="lg"
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {modalMode === 'add' ? <PlusIcon /> : <PencilIcon />}
                        {modalMode === 'add' ? 'Yeni Hizmet Tanımla' : `${currentService.name} — Düzenle`}
          </span>
                }
                footer={
                    <>
                        <button className="as-btn as-btn-ghost" onClick={() => setIsModalVisible(false)}>İptal</button>
                        <button className="as-btn as-btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? <SpinnerIcon size={15} /> : null}
                            {modalMode === 'add' ? 'Hizmeti Oluştur' : 'Değişiklikleri Kaydet'}
                        </button>
                    </>
                }
            >
                {/* ── TABS ── */}
                <div className="as-tabs">
                    <button className={`as-tab-btn ${activeTab === 1 ? 'active' : ''}`} onClick={() => setActiveTab(1)}>
                        <FileTextIcon /> Temel Bilgiler
                    </button>
                    <button className={`as-tab-btn ${activeTab === 2 ? 'active' : ''}`} onClick={() => setActiveTab(2)}>
                        <UsersIcon /> Personel Fiyatları
                    </button>
                </div>

                {/* ── TAB 1: TEMEL BİLGİLER ── */}
                <div style={{ display: activeTab === 1 ? 'block' : 'none' }}>
                    <div className="as-form-group">
                        <label className="as-label">Hizmet Adı</label>
                        <input
                            className="as-input"
                            name="name"
                            placeholder="Örn: Saç Kesimi"
                            value={currentService.name}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div className="as-form-group" style={{ marginBottom: 0 }}>
                            <label className="as-label">Varsayılan Fiyat</label>
                            <div className="as-input-group">
                                <span className="as-prefix">₺</span>
                                <input
                                    className="as-input"
                                    type="number"
                                    name="price"
                                    value={currentService.price}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className="as-form-group" style={{ marginBottom: 0 }}>
                            <label className="as-label">Varsayılan Süre</label>
                            <div className="as-input-group">
                                <span className="as-prefix">dk</span>
                                <input
                                    className="as-input"
                                    type="number"
                                    name="duration"
                                    value={currentService.duration}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="as-form-group">
                        <label className="as-label">Hedef Kitle (Cinsiyet)</label>
                        <div className="as-gender-group">
                            {[
                                { val: 'unisex', label: 'Unisex' },
                                { val: 'male',   label: 'Erkek' },
                                { val: 'female', label: 'Kadın' },
                            ].map(opt => (
                                <div
                                    key={opt.val}
                                    className={`as-gender-opt ${currentService.target_gender === opt.val ? `active-${opt.val}` : ''}`}
                                    onClick={() => setCurrentService(p => ({ ...p, target_gender: opt.val }))}
                                >
                                    {opt.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="as-form-group">
                        <label className="as-label">Açıklama (Opsiyonel)</label>
                        <textarea
                            className="as-textarea"
                            name="description"
                            rows={2}
                            placeholder="Hizmet hakkında kısa bir açıklama..."
                            value={currentService.description || ''}
                            onChange={handleInputChange}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Toggle
                            id="optionalSwitch"
                            checked={currentService.optional}
                            onChange={(e) => setCurrentService(p => ({ ...p, optional: e.target.checked }))}
                            label="Opsiyonel Ek Hizmet"
                            sublabel="Bu hizmet tek başına seçilemez, ana hizmetin yanına eklenir."
                        />
                        <Toggle
                            id="favoriteSwitch"
                            checked={currentService.is_favorite || false}
                            onChange={(e) => setCurrentService(p => ({ ...p, is_favorite: e.target.checked }))}
                            label="⭐ Favori / Sık Kullanılan"
                            sublabel="Adisyon ve listeleme ekranlarında en üstte vurgulanır."
                        />
                    </div>

                    {/* ── Toggle names need special handling ── */}
                    <input name="optional"    type="checkbox" checked={currentService.optional}         onChange={handleInputChange} style={{ display: 'none' }} id="optionalSwitch" />
                    <input name="is_favorite" type="checkbox" checked={currentService.is_favorite||false} onChange={handleInputChange} style={{ display: 'none' }} id="favoriteSwitch" />
                </div>

                {/* ── TAB 2: PERSONEL FİYATLARI ── */}
                <div style={{ display: activeTab === 2 ? 'block' : 'none' }}>
                    <div className="as-info-box">
                        <InfoIcon />
                        <span>Alanları boş bıraktığınız personeller, hizmetin varsayılan fiyatını ve süresini kullanır.</span>
                    </div>

                    <div className="as-staff-table-wrap">
                        <table className="as-staff-table">
                            <thead>
                            <tr>
                                <th>Personel</th>
                                <th>Özel Fiyat (₺)</th>
                                <th>Özel Süre (dk)</th>
                            </tr>
                            </thead>
                            <tbody>
                            {staffPricingList.map(staff => (
                                <tr key={staff.staff_id}>
                                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 600, color: 'var(--as-text1)' }}>
                        <UserIcon size={13} /> {staff.staff_name}
                      </span>
                                    </td>
                                    <td>
                                        <div className="as-input-group" style={{ maxWidth: 130 }}>
                                            <span className="as-prefix">₺</span>
                                            <input
                                                className="as-input"
                                                type="number"
                                                placeholder={currentService.price.toString()}
                                                value={staff.custom_price === null ? '' : staff.custom_price}
                                                onChange={e => handleStaffPriceChange(staff.staff_id, 'custom_price', e.target.value)}
                                                style={{ padding: '7px 10px' }}
                                            />
                                        </div>
                                    </td>
                                    <td>
                                        <div className="as-input-group" style={{ maxWidth: 120 }}>
                                            <span className="as-prefix"><ClockIcon size={13} /></span>
                                            <input
                                                className="as-input"
                                                type="number"
                                                placeholder={currentService.duration.toString()}
                                                value={staff.custom_duration === null ? '' : staff.custom_duration}
                                                onChange={e => handleStaffPriceChange(staff.staff_id, 'custom_duration', e.target.value)}
                                                style={{ padding: '7px 10px' }}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            {/* ─────────────────────────────────────────────────────────────────────────
         SİLME ONAY MODAL
      ───────────────────────────────────────────────────────────────────────── */}
            <Modal
                visible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                size="sm"
                title="Hizmeti Sil"
                footer={
                    <>
                        <button className="as-btn as-btn-ghost" onClick={() => setIsDeleteModalVisible(false)}>Vazgeç</button>
                        <button className="as-btn as-btn-danger" onClick={handleDelete} disabled={deleting}>
                            {deleting ? <SpinnerIcon size={15} /> : <TrashIcon />}
                            Evet, Sil
                        </button>
                    </>
                }
            >
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                    <div className="as-delete-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                        </svg>
                    </div>
                    <h3 className="as-delete-title">Emin misiniz?</h3>
                    <p className="as-delete-sub">
                        <strong>"{serviceToDelete?.name}"</strong> hizmetini siliyorsunuz.
                    </p>
                    <p style={{ fontSize: '0.78rem', color: 'var(--as-error)', margin: 0, fontWeight: 600 }}>
                        Bu işlem geri alınamaz.
                    </p>
                </div>
            </Modal>
        </div>
    )
}

export default AdminServices